import {
  AnchoredPanel,
  AnchoredPanelType,
  ButtonType,
  FormValues,
  InputForm,
  InputFormComponent,
  PanelFooterButton,
  SimpleBuilder,
  TextInput,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { Icon, Label, Link, mergeStyleSets, Stack, Text } from "@fluentui/react";
import * as Messages from "../../codegen/Messages";
import { tagsLearnMoreUrl } from "../../constants/docConstants";
import { areTagsEqual } from "../../helpers/resourceHelper";
import { validateTagKey } from "../../helpers/validationHelper";

export interface TagsEditPanelProps {
  targetId: string;
  title: string;
  tagsDefaultValue: { name: string, value: string }[];
  onSubmit: (formValues: FormValues) => void;
  onClose?: () => void;
}

export const enum Fields {
  Tags = "tags",
  Name = "name",
  Value = "value",
}

const classNames = mergeStyleSets({
  nameInput: { width: "50%", minWidth: "107px" },
  valueInput: { width: "50%", minWidth: "107px" },
});

export const TagsEditPanel = (
  { targetId, title, onSubmit, onClose, tagsDefaultValue }: TagsEditPanelProps,
): JSX.Element => {
  const [key] = React.useState<string>(uniqueGUID());
  const [valueChanged, setValueChanged] = React.useState<boolean>(false);

  const formComponentRef = React.useRef<InputFormComponent>();
  const setFormComponentRef = (formComponent: InputFormComponent): void => {
    formComponentRef.current = formComponent;
  };

  const onClick = (): void => {
    formComponentRef.current?.enableValidation();
    formComponentRef.current?.submit();
  };

  const validateValueChanged = (
    fieldValue: { name: string, value: string }[],
    defaultFieldValue: { name: string, value: string }[],
  ): void => {
    const compareResult = areTagsEqual(fieldValue, defaultFieldValue);
    if (compareResult) {
      setValueChanged(false);
    } else {
      setValueChanged(true);
    }
  };

  const footerButtons: PanelFooterButton[] = [
    {
      type: ButtonType.PRIMARY,
      label: Messages.actions.save(),
      disabled: !valueChanged,
      onClick,
      style: { margin: "10px 5px 0px 0px", height: "24px" },
    },
    {
      type: ButtonType.DEFAULT,
      label: Messages.common.cancel(),
      onClick: () => { onClose?.(); },
      style: { margin: "10px 5px 0px 0px", height: "24px" },
    },
  ];

  return (
    <AnchoredPanel
      title={title}
      onClose={onClose}
      isOpen
      footer={footerButtons}
      anchorId={targetId}
      type={AnchoredPanelType.MEDIUM}
    >
      <InputForm
        componentRef={setFormComponentRef}
        onSubmit={onSubmit}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onFormChange={(_formValues: FormValues, fieldValue: any, field: string) => {
          if (field === Fields.Tags) {
            validateValueChanged(fieldValue, tagsDefaultValue);
          }
        }}
      >
        <Stack style={{ margin: "10px 0px" }}>
          <Text>{Messages.tags.description()}</Text>
          <Link href={tagsLearnMoreUrl} target="_blank">
            <>
              <span>{Messages.tags.info.learnMore()}</span>
              <Icon style={{ paddingLeft: "5px" }} iconName="OpenInNewWindow" />
            </>
          </Link>
        </Stack>
        <br />
        <Stack style={{ paddingBottom: "20px" }}>
          <SimpleBuilder
            fieldName={Fields.Tags}
            defaultValue={tagsDefaultValue}
            key={key}
          >
            <TextInput
              label={Messages.common.name()}
              fieldName={Fields.Name}
              className={classNames.nameInput}
              validator={validateTagKey}
            />
            <Label>:</Label>
            <TextInput
              label={Messages.common.value()}
              fieldName={Fields.Value}
              className={classNames.valueInput}
            />
          </SimpleBuilder>
        </Stack>
      </InputForm>
    </AnchoredPanel>
  );
};
